"use client";
import { Sentence } from "@/api/words/getWordDetail";
import { buildRoute, links } from "@/utils/links";
import { ChevronDown, ChevronUp, LinkIcon, Quote } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { LocalizedContent } from "@/types/language";

interface ExampleCardProps {
  sentence: Sentence;
  orderNumber: number;
  interfaceLanguage: string;
  learningLanguage: string;
  content: LocalizedContent;
}

export const ExampleCard = ({
  sentence,
  orderNumber,
  interfaceLanguage,
  learningLanguage,
  content,
}: ExampleCardProps) => {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => setExpanded(!expanded);
  const getLink = buildRoute(links.word, {
    interfaceLanguage,
    learningLanguage,
  });

  return (
    <div className="group watercolor-card hover:shadow-ink-splash transition-all duration-300 transform hover:-translate-y-1">
      {/* Main Example Card */}
      <div className="relative bg-gradient-to-br from-periwinkle/5 via-white to-peach-sand/5 p-8">
        {/* Decorative quote icon */}
        <div className="absolute top-4 left-4 opacity-20">
          <Quote className="h-8 w-8 text-periwinkle" />
        </div>
        
        <div className="flex items-start gap-6">
          <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-periwinkle to-periwinkle/80 rounded-full flex items-center justify-center shadow-lg">
            <span className="text-white font-bold text-lg">{orderNumber}</span>
          </div>
          
          <div className="flex-grow space-y-4">
            <div className="relative">
              <p className="text-xl text-ink-dark font-medium leading-relaxed pl-6" style={{ fontFamily: 'Georgia, serif' }}>
                &ldquo;{sentence.sentence}&rdquo;
              </p>
              <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-periwinkle to-peach-sand rounded-full"></div>
            </div>
            
            {sentence.translation?.trim() && (
              <div className="bg-gradient-to-r from-coral-splash/10 to-leaf-green/10 p-4 rounded-xl border-l-3 border-coral-splash/40">
                <p className="text-lg text-ink-dark/80 italic">
                  {sentence.translation}
                </p>
              </div>
            )}
            
            {sentence.description && (
              <div className="bg-peach-sand/10 p-4 rounded-xl">
                <p className="text-sm text-ink-dark/70 italic">
                  <strong>{content.page_word_example_context_label || 'Context:'}</strong> {sentence.description}
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-shrink-0 gap-2">
            {sentence.linkedWords && sentence.linkedWords.length > 0 && (
              <button
                onClick={toggleExpanded}
                className="p-3 hover:bg-white/80 rounded-full transition-all duration-300 group-hover:opacity-100 watercolor-button"
                title={expanded ? content.page_word_example_hide_details || 'Hide details' : content.page_word_example_show_details || 'Show details'}
              >
                {expanded ? (
                  <ChevronUp className="h-5 w-5 text-white" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-white" />
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Linked Words Section */}
      {expanded && sentence.linkedWords && (
        <div className="border-t border-periwinkle/20">
          <div className="p-6 bg-gradient-to-r from-white to-periwinkle/5">
            <div className="flex items-center gap-3 mb-4">
              <LinkIcon className="h-5 w-5 text-periwinkle" />
              <h4 className="text-lg font-medium text-ink-dark" style={{ fontFamily: 'Georgia, serif' }}>
                {content.page_word_example_connected_words_title || 'Connected Words'}
              </h4>
            </div>
            <div className="flex flex-wrap gap-3">
              {sentence.linkedWords.map((word) =>
                word.isPublished ? (
                  <Link
                    href={getLink({ wordSlug: word.slug })}
                    key={word.id}
                    className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-periwinkle/20 to-peach-sand/20 hover:from-periwinkle/30 hover:to-peach-sand/30 text-ink-dark font-medium transition-all duration-300 transform hover:-translate-y-0.5 border border-periwinkle/30"
                  >
                    {word.name}
                  </Link>
                ) : (
                  <span
                    key={word.id}
                    className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-periwinkle/20 to-peach-sand/20 text-ink-dark font-medium border border-periwinkle/30"
                  >
                    {word.name}
                  </span>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};